import React, { useCallback, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import {
  Bar, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import {
  CalendarRange, ChevronLeft, ChevronRight, FileText, Link2, Lock, Megaphone, Pencil, PlayCircle, Plus, QrCode, StickyNote,
} from 'lucide-react';

import { loadStorefrontCampaign, loadStorefrontCampaigns, loadStorefrontFilters, updateStorefrontCampaign } from './api';
import { Empty, Pill, WorkspaceTabs } from './parts';
import { countryName } from './countries';
import { errorMessage } from './ui';
import { useLiveResource } from './useLive';
import {
  CHART, ChartTip, Flag, KpiRow, NUMBER, Panel, Share, pointLabel, tipPoint,
} from './MobileParts';
import {
  ChannelMark, DeviceMark, Funnel, PathText, ReadState, StoreTable, StorefrontGate, StorefrontHead, addMoney, codeText, count,
  dateTime, deviceLabel, money, moneyOf, otherMoney, primaryMoney, rate, ratio, shortDate, st, stCount, useStorefrontAccess, useStorefrontSite,
} from './StorefrontParts';
import { stageLabel } from './StorefrontOverview';
import { sessionLink } from './StorefrontLive';
import { CampaignDialog, LinkDialog, QrDialog, QrImage, UrlLine } from './StorefrontCampaignForms';

// Campaign links: a link for a post, an ad, a newsletter or an influencer, and
// who came through it, what they did and whether they bought. There are no
// rewards; a link only tells where a visit came from.

const STATUS_TONE = { active: 'ok', paused: 'warn', archived: 'flat' };

function spendText(spend) {
  return spend ? moneyOf(spend, 'amountMinor') : st('campaigns.no_spend');
}

function roasText(performance) {
  return performance.roas === null ? st('none') : st('campaigns.roas_value', { value: performance.roas.toFixed(performance.roas >= 10 ? 0 : 1) });
}

function datesText(campaign) {
  if (campaign.startsAt === null && campaign.endsAt === null) return st('campaigns.no_dates');
  if (campaign.endsAt === null) return st('campaigns.from_date', { date: shortDate(campaign.startsAt) });
  if (campaign.startsAt === null) return st('campaigns.until_date', { date: shortDate(campaign.endsAt) });
  return st('campaigns.date_span', { from: shortDate(campaign.startsAt), to: shortDate(campaign.endsAt) });
}

// Every campaign on one line, the same columns on every line: what it is, the
// visits it brought against the busiest one, what they bought, and what it cost.
function CampaignList({ campaigns, selected, onPick }) {
  if (campaigns.length === 0) return <Empty title={st('campaigns.none')} detail={st('campaigns.none_detail')} />;
  const most = Math.max(1, ...campaigns.map((campaign) => campaign.performance.sessions));
  return (
    <div className="xo-sf-camps" role="table" aria-label={st('campaigns.list_title')}>
      <div className="xo-sf-camps-head" role="row">
        <span role="columnheader">{st('campaigns.col_campaign')}</span>
        <span role="columnheader">{st('campaigns.col_sessions')}</span>
        <span role="columnheader" className="is-end">{st('campaigns.col_orders')}</span>
        <span role="columnheader" className="is-end">{st('campaigns.col_revenue')}</span>
        <span role="columnheader" className="is-end is-spend">{st('campaigns.col_spend')}</span>
        <span aria-hidden="true" />
      </div>
      {campaigns.map((campaign) => {
        const performance = campaign.performance;
        const revenue = primaryMoney(performance.revenue);
        return (
          <button key={campaign.id} type="button" role="row" onClick={() => onPick(campaign.id)}
            className={`xo-sf-camps-row${selected === campaign.id ? ' is-active' : ''}`}>
            <span className="xo-sf-camps-name" role="cell">
              <strong title={campaign.name}>{campaign.name}</strong>
              <small><ChannelMark channel={campaign.channel} /><Pill tone={STATUS_TONE[campaign.status]}>{st(`campaigns.status_${campaign.status}`)}</Pill></small>
            </span>
            <span className="xo-sf-camps-visits" role="cell">
              <span><b>{count(performance.sessions)}</b><Share value={performance.sessions} total={most} /></span>
              <small>{st('campaigns.opens_line', { count: NUMBER.format(performance.hits) })}</small>
            </span>
            <span className="xo-sf-camps-figure is-end" role="cell">
              <b>{count(performance.orders)}</b>
              <small>{performance.conversionRate === null ? st('campaigns.no_visits_short') : st('campaigns.converted', { rate: ratio(performance.conversionRate) })}</small>
            </span>
            <span className="xo-sf-camps-figure is-end" role="cell">
              <b className={revenue ? '' : 'is-muted'}>{revenue ? moneyOf(revenue) : st('campaigns.none_yet')}</b>
              <small>{otherMoney(performance.revenue).length > 0 ? stCount('more_currencies', otherMoney(performance.revenue).length) : ' '}</small>
            </span>
            <span className="xo-sf-camps-figure is-end is-spend" role="cell">
              <b className={campaign.spend ? '' : 'is-muted'}>{campaign.spend ? spendText(campaign.spend) : st('campaigns.not_set')}</b>
              <small>{campaign.spend && performance.roas !== null ? st('campaigns.return_line', { value: performance.roas.toFixed(performance.roas >= 10 ? 0 : 1) }) : ' '}</small>
            </span>
            <ChevronRight aria-hidden="true" className="xo-sf-camps-go" />
          </button>
        );
      })}
    </div>
  );
}

function CampaignChart({ series, step }) {
  if (series.every((point) => point.hits === 0 && point.sessions === 0)) return <Empty title={st('campaigns.no_visits')} />;
  return (
    <div className="xo-mob-chart is-short">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={series} margin={{ top: 8, right: 18, left: 0, bottom: 0 }}>
          <CartesianGrid vertical={false} stroke={CHART.grid} />
          <XAxis dataKey="at" tickFormatter={pointLabel(step)} tickLine={false} axisLine={false} minTickGap={24} tick={CHART.tick} />
          <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={CHART.tick} width={32} />
          <Tooltip cursor={CHART.cursor} animationDuration={120} content={(
            <ChartTip title={tipPoint(step)} rows={(point) => [
              { name: st('campaigns.col_sessions'), color: CHART.pale, mark: 'bar', text: NUMBER.format(point.sessions) },
              { name: st('campaigns.col_opens'), color: CHART.mid, mark: 'line', text: NUMBER.format(point.hits) },
              { name: st('campaigns.col_orders'), color: CHART.ink, mark: 'line', text: NUMBER.format(point.orders) },
            ]} />
          )} />
          <Bar dataKey="sessions" fill={CHART.pale} radius={[3, 3, 0, 0]} maxBarSize={22} name={st('campaigns.col_sessions')} />
          <Line type="monotone" dataKey="hits" stroke={CHART.mid} strokeWidth={1.8} dot={false} name={st('campaigns.col_opens')} />
          <Line type="monotone" dataKey="orders" stroke={CHART.ink} strokeWidth={2.2} dot={false} name={st('campaigns.col_orders')} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

function LinkCards({ links, campaign, manage, onQr, onEdit }) {
  if (links.length === 0) return <Empty title={st('links.none')} detail={manage ? st('links.none_detail') : null} />;
  return (
    <ol className="xo-sf-links">
      {links.map((link) => {
        const revenue = primaryMoney(link.performance.revenue);
        return (
          <li key={link.id}>
            <button type="button" className="xo-sf-link-qr" onClick={() => onQr(link)} aria-label={st('links.open_qr', { code: link.code })}>
              <QrImage url={link.url} size={72} label={st('links.qr_label', { url: link.url })} />
            </button>
            <div className="xo-sf-link-main">
              <header>
                <strong title={link.label || link.code}>{link.label || link.code}</strong>
                {manage && <button type="button" className="xo-mob-icon-button" onClick={() => onEdit(link)} aria-label={st('links.edit_title')}><Pencil /></button>}
              </header>
              <span className="xo-sf-link-dest" title={link.destinationPath}>{st('links.opens_page', { path: link.destinationPath })}</span>
              {Object.values(link.utm).some(Boolean) && (
                <span className="xo-sf-utm-chips">
                  {Object.entries(link.utm).filter(([, value]) => value).map(([name, value]) => <em key={name}><i>{st(`links.utm_${name}`)}</i>{value}</em>)}
                </span>
              )}
              <UrlLine label={st('links.short_url')} url={link.url} />
            </div>
            <div className="xo-sf-link-side">
              <dl className="xo-sf-link-stats">
                <div><dt>{st('campaigns.col_opens')}</dt><dd>{count(link.performance.hits)}</dd></div>
                <div><dt>{st('campaigns.col_sessions')}</dt><dd>{count(link.performance.sessions)}</dd></div>
                <div><dt>{st('campaigns.col_orders')}</dt><dd>{count(link.performance.orders)}</dd></div>
                <div><dt>{st('campaigns.col_revenue')}</dt>
                  <dd className={revenue ? '' : 'is-muted'} title={link.performance.revenue.map((entry) => moneyOf(entry)).join(' · ')}>
                    {revenue ? moneyOf(revenue, 'netMinor', { compact: true }) : st('none')}
                  </dd></div>
              </dl>
              <Link className="xo-mob-link xo-sf-link-sessions" to={`/xomics/store/sessions?campaign=${campaign.id}&link=${link.id}`}>
                <PlayCircle aria-hidden="true" />{st('links.sessions')}
              </Link>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

// Order references open an order's public tracking page, so only staff who can
// read orders get them; for everyone else the server sends none.
export function OrderReference({ reference }) {
  if (reference === null || reference === undefined) {
    return <span className="xo-sf-hidden-ref" title={st('reference_hidden_detail')}><Lock aria-hidden="true" />{st('reference_hidden')}</span>;
  }
  return <code className="xo-sf-code">{reference}</code>;
}

function Orders({ orders }) {
  const columns = [
    { key: 'reference', label: st('orders.reference'), primary: true, render: (row) => <OrderReference reference={row.reference} />, sortValue: (row) => row.reference || '' },
    { key: 'placedAt', label: st('orders.placed'), render: (row) => dateTime(row.placedAt), sortValue: (row) => row.placedAt },
    { key: 'status', label: st('orders.status'), wide: true, render: (row) => codeText('order_status', row.status), sortValue: (row) => row.status },
    { key: 'link', label: st('orders.link'), wide: true, render: (row) => (row.link ? <code className="xo-sf-code">{row.link.code}</code> : st('none')), sortValue: (row) => (row.link ? row.link.code : '') },
    {
      key: 'total', label: st('orders.total'), align: 'end', sortValue: (row) => row.totalMinor - row.refundedMinor,
      render: (row) => (
        <span className="xo-sf-money">{money(row.totalMinor - row.refundedMinor, row.currency, { minorUnit: row.minorUnit })}
          {row.refundedMinor > 0 && <small>{st('orders.refunded', { amount: moneyOf(row, 'refundedMinor') })}</small>}</span>
      ),
    },
    { key: 'session', label: st('orders.session'), sortable: false, render: (row) => <Link className="xo-mob-link" to={sessionLink(row.sessionId)}>{st('orders.open_session')}<ChevronRight aria-hidden="true" /></Link> },
  ];
  return <StoreTable columns={columns} rows={orders} rowKey={(row) => row.reference || `${row.placedAt}-${row.sessionId}`} limit={10} initialSort={{ key: 'placedAt', direction: 'desc' }}
    empty={<Empty title={st('campaigns.no_orders')} />} />;
}

// Where a campaign's visits landed, where they are and on what, as the Mobile section lists places.
function Breakdown({ title, rows }) {
  const most = Math.max(1, ...rows.map((row) => row.sessions));
  return (
    <Panel title={title}>
      {rows.length === 0 ? <Empty title={st('campaigns.no_visits')} /> : (
        <ol className="xo-mob-places xo-sf-compact-list">
          {rows.map((row) => (
            <li key={row.key}>
              {row.icon}
              <span>{row.name}<small>{row.detail}</small></span>
              <Share value={row.sessions} total={most} />
              <em>{stCount('sessions_count', row.sessions)}</em>
            </li>
          ))}
        </ol>
      )}
    </Panel>
  );
}

function Breakdowns({ data }) {
  return (
    <div className="xo-mob-row is-thirds is-even xo-sf-thirds">
      <Breakdown title={st('campaigns.landing_title')} rows={data.landingPages.slice(0, 6).map((row) => ({
        key: row.path, icon: <FileText aria-hidden="true" className="xo-sf-list-icon" />, name: <PathText path={row.path} />,
        detail: stCount('orders_count', row.orders), sessions: row.sessions,
      }))} />
      <Breakdown title={st('campaigns.countries_title')} rows={data.countries.slice(0, 6).map((row) => ({
        key: row.code, icon: <Flag code={row.code} />, name: <strong>{countryName(row.code)}</strong>,
        detail: stCount('daily_visitors_count', row.visitors), sessions: row.sessions,
      }))} />
      <Breakdown title={st('campaigns.devices_title')} rows={data.devices.map((row) => ({
        key: row.deviceType || 'unknown', icon: <DeviceMark device={row.deviceType} />, name: <strong>{deviceLabel(row.deviceType)}</strong>,
        detail: stCount('daily_visitors_count', row.visitors), sessions: row.sessions,
      }))} />
    </div>
  );
}

// One campaign: its numbers, its funnel, its links with their QR codes, and its orders.
function CampaignDetail({ id, days, site, manage, onBack, onChanged, openDialog }) {
  const load = useCallback(() => loadStorefrontCampaign(id, days), [id, days]);
  const report = useLiveResource(['store', 'store_traffic'], load, { reconcileMs: 60000 });
  const [statusError, setStatusError] = useState(null);
  if (report.data === null) return <ReadState resource={report} label={st('campaigns.reading_one')} />;
  const data = report.data;
  const { campaign, performance } = data;
  const refresh = () => { report.refresh(); onChanged(); };

  async function setStatus(status) {
    setStatusError(null);
    try {
      await updateStorefrontCampaign(campaign.id, { status });
      refresh();
    } catch (error) {
      setStatusError(errorMessage(error));
    }
  }

  return (
    <div className="xo-sf-campaign">
      <section className="xo-sf-campaign-head">
        <button type="button" className="xo-mob-back" onClick={onBack}><ChevronLeft aria-hidden="true" />{st('campaigns.back')}</button>
        <div className="xo-sf-campaign-title">
          <span className="xo-mob-dialog-icon"><Megaphone aria-hidden="true" /></span>
          <div>
            <h2>{campaign.name}</h2>
            <p>
              <ChannelMark channel={campaign.channel} />
              <Pill tone={STATUS_TONE[campaign.status]}>{st(`campaigns.status_${campaign.status}`)}</Pill>
              <span><CalendarRange aria-hidden="true" />{datesText(campaign)}</span>
              {campaign.createdBy && <span>{st('campaigns.created_by', { name: campaign.createdBy.name, date: shortDate(campaign.createdAt) })}</span>}
            </p>
          </div>
          {manage && (
            <div className="xo-sf-campaign-actions">
              {campaign.status !== 'active' && <button type="button" className="xo-btn-2" onClick={() => setStatus('active')}>{st('campaigns.activate')}</button>}
              {campaign.status === 'active' && <button type="button" className="xo-btn-2" onClick={() => setStatus('paused')}>{st('campaigns.pause')}</button>}
              {campaign.status !== 'archived' && <button type="button" className="xo-btn-2" onClick={() => setStatus('archived')}>{st('campaigns.archive')}</button>}
              <button type="button" className="xo-btn-2" onClick={() => openDialog({ kind: 'campaign', campaign, onSaved: refresh })}><Pencil aria-hidden="true" />{st('campaigns.edit')}</button>
              <button type="button" className="xo-btn" onClick={() => openDialog({ kind: 'link', campaign, onSaved: refresh })}><Plus aria-hidden="true" />{st('links.new_title')}</button>
            </div>
          )}
        </div>
        {campaign.notes && <p className="xo-sf-notes"><StickyNote aria-hidden="true" />{campaign.notes}</p>}
        {statusError && <p className="xo-alert t-bad" role="alert">{statusError}</p>}
      </section>

      <KpiRow items={[
        { label: st('campaigns.col_opens'), value: count(performance.hits), detail: st('campaigns.opens_detail') },
        { label: st('campaigns.col_sessions'), value: count(performance.sessions), detail: stCount('daily_visitors_count', performance.visitors), trend: data.series.map((point) => point.sessions) },
        { label: st('campaigns.col_orders'), value: count(performance.orders), detail: st('campaigns.conversion_detail', { rate: ratio(performance.conversionRate) }), trend: data.series.map((point) => point.orders) },
        {
          label: st('campaigns.col_revenue'), value: primaryMoney(performance.revenue) ? moneyOf(primaryMoney(performance.revenue)) : st('no_revenue'),
          detail: otherMoney(performance.revenue).length > 0 ? otherMoney(performance.revenue).map((entry) => moneyOf(entry)).join(' · ') : st('campaigns.revenue_detail'),
        },
        {
          // Return is revenue in the spend's own currency; with none in it yet, the tile says so briefly.
          label: st('campaigns.col_roas'), value: !campaign.spend ? st('campaigns.not_set') : performance.roas === null ? st('campaigns.return_none') : roasText(performance),
          detail: !campaign.spend ? st('campaigns.no_spend')
            : performance.roas === null ? st('campaigns.roas_none_short', { currency: campaign.spend.currency, spend: spendText(campaign.spend) })
              : st('campaigns.spend_detail', { spend: spendText(campaign.spend), cost: performance.costPerOrder ? moneyOf(performance.costPerOrder, 'amountMinor') : st('none') }),
        },
      ]} />

      <div className="xo-mob-row is-wide-left is-even">
        <Panel title={st('campaigns.funnel_title')} detail={st('campaigns.funnel_detail')}>
          <Funnel compact steps={data.funnel.map((step) => ({ key: step.stage, label: stageLabel(step.stage), count: step.sessions }))} />
        </Panel>
        <Panel title={st('campaigns.over_time')} detail={st('campaigns.over_time_detail')}>
          <CampaignChart series={data.series} step={data.step} />
        </Panel>
      </div>

      <Panel title={st('links.title')} detail={st('links.detail', { origin: site.publicOrigin })}
        aside={manage && <button type="button" className="xo-btn" onClick={() => openDialog({ kind: 'link', campaign, onSaved: refresh })}><Plus aria-hidden="true" />{st('links.new_title')}</button>}>
        <LinkCards links={data.links} campaign={campaign} manage={manage}
          onQr={(link) => openDialog({ kind: 'qr', link, campaign })}
          onEdit={(link) => openDialog({ kind: 'link', campaign, link, onSaved: refresh })} />
      </Panel>

      <Panel title={st('campaigns.orders_title')} detail={st('campaigns.orders_detail')}>
        <Orders orders={data.orders} />
      </Panel>

      <Breakdowns data={data} />
    </div>
  );
}

function CampaignsView({ site, store }) {
  const [days, setDays] = useState(30);
  const [status, setStatus] = useState('all');
  const [dialog, setDialog] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const access = useStorefrontAccess();
  const selected = searchParams.get('campaign') ? Number(searchParams.get('campaign')) : null;
  const load = useCallback(() => loadStorefrontCampaigns({ site: site.code, days }), [site.code, days]);
  const list = useLiveResource(['store', 'store_traffic'], load, { reconcileMs: 60000 });
  const loadFilters = useCallback(() => loadStorefrontFilters({ site: site.code, days }), [site.code, days]);
  const filters = useLiveResource(['store'], loadFilters, { reconcileMs: 0, enabled: access.campaigns });

  const pick = useCallback((id) => {
    const next = new URLSearchParams(searchParams);
    if (id === null) next.delete('campaign'); else next.set('campaign', String(id));
    setSearchParams(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [searchParams, setSearchParams]);

  const campaigns = useMemo(() => (list.data ? list.data.campaigns : []), [list.data]);
  const shown = status === 'all' ? campaigns : campaigns.filter((campaign) => campaign.status === status);
  const channels = filters.data ? filters.data.channels : [];
  const currencies = filters.data ? filters.data.currencies : [];
  const totals = useMemo(() => ({
    hits: campaigns.reduce((sum, campaign) => sum + campaign.performance.hits, 0),
    sessions: campaigns.reduce((sum, campaign) => sum + campaign.performance.sessions, 0),
    orders: campaigns.reduce((sum, campaign) => sum + campaign.performance.orders, 0),
    revenue: addMoney(campaigns.map((campaign) => campaign.performance.revenue)),
    active: campaigns.filter((campaign) => campaign.status === 'active').length,
  }), [campaigns]);

  const closeDialog = useCallback(() => setDialog(null), []);
  const head = (
    <StorefrontHead title={st('campaigns.title')} subtitle={st('campaigns.subtitle')} days={days} onDays={setDays} store={store}>
      {access.campaigns && (
        <button type="button" className="xo-btn xo-sf-head-action" disabled={channels.length === 0}
          onClick={() => setDialog({ kind: 'campaign', campaign: null, onSaved: (campaign) => { list.refresh(); pick(campaign.id); } })}>
          <Plus aria-hidden="true" />{st('campaigns.new_title')}
        </button>
      )}
    </StorefrontHead>
  );

  let body;
  if (selected !== null) {
    body = <CampaignDetail key={selected} id={selected} days={days} site={site} manage={access.campaigns}
      onBack={() => pick(null)} onChanged={list.refresh} openDialog={setDialog} />;
  } else if (list.data === null) {
    body = <ReadState resource={list} label={st('campaigns.reading')} />;
  } else {
    body = (
      <>
        <KpiRow items={[
          { label: st('campaigns.kpi_campaigns'), value: count(campaigns.length), detail: st('campaigns.kpi_active', { count: NUMBER.format(totals.active) }) },
          { label: st('campaigns.col_opens'), value: count(totals.hits), detail: st('campaigns.opens_detail') },
          { label: st('campaigns.kpi_sessions'), value: count(totals.sessions), detail: stCount('orders_count', totals.orders) },
          {
            // One figure per tile: the main currency's, the others named in the line under it.
            label: st('campaigns.kpi_revenue'), value: primaryMoney(totals.revenue) ? moneyOf(primaryMoney(totals.revenue)) : st('no_revenue'),
            detail: [otherMoney(totals.revenue).map((entry) => moneyOf(entry)).join(' · '), st('campaigns.kpi_conversion', { rate: rate(totals.orders, totals.sessions) })].filter(Boolean).join(' · '),
          },
        ]} />
        <Panel title={st('campaigns.list_title')} detail={st('campaigns.list_detail')}
          aside={(
            <WorkspaceTabs value={status} onChange={setStatus} label={st('campaigns.field_status')} className="is-compact xo-mob-tabs xo-sf-status-tabs" tabs={[
              { value: 'all', label: st('campaigns.status_all'), count: campaigns.length },
              ...['active', 'paused', 'archived'].map((code) => ({ value: code, label: st(`campaigns.status_${code}`), count: campaigns.filter((campaign) => campaign.status === code).length })),
            ]} />
          )}>
          <CampaignList campaigns={shown} selected={selected} onPick={pick} />
        </Panel>
        <div className="xo-sf-howto">
          <span className="xo-mob-dialog-icon"><QrCode aria-hidden="true" /></span>
          <div>
            <strong>{st('campaigns.howto_title')}</strong>
            <p>{st('campaigns.howto_detail', { origin: site.publicOrigin })}</p>
          </div>
          <Link2 aria-hidden="true" className="xo-sf-howto-mark" />
        </div>
      </>
    );
  }

  return (
    <div className="xo-platform-page xo-mobile-page xo-sf-page">
      {head}
      {list.error && list.data !== null && <p className="xo-alert t-warn" role="alert">{list.error}</p>}
      {body}
      <AnimatePresence>
        {dialog && dialog.kind === 'campaign' && (
          <CampaignDialog key="campaign" site={site} campaign={dialog.campaign} channels={dialog.campaign && !channels.includes(dialog.campaign.channel) ? [dialog.campaign.channel, ...channels] : channels}
            currencies={currencies} onClose={closeDialog} onSaved={(campaign) => { setDialog(null); dialog.onSaved(campaign); }} />
        )}
        {dialog && dialog.kind === 'link' && (
          <LinkDialog key="link" site={site} campaign={dialog.campaign} link={dialog.link || null} onClose={closeDialog}
            onSaved={(link) => { dialog.onSaved(link); if (dialog.link) setDialog(null); }} />
        )}
        {dialog && dialog.kind === 'qr' && <QrDialog key="qr" link={dialog.link} campaign={dialog.campaign} onClose={closeDialog} />}
      </AnimatePresence>
    </div>
  );
}

export default function StorefrontCampaigns() {
  const store = useStorefrontSite();
  return <StorefrontGate store={store}>{(site) => <CampaignsView site={site} store={store} />}</StorefrontGate>;
}
